import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/** Breadcrumb adımı: {@code to} verilirse link, verilmezse düz metin. */
export interface Crumb {
  label: string
  to?: string
}

interface Ctx {
  trail: Crumb[]
  setTrail: (t: Crumb[]) => void
}

const BreadcrumbContext = createContext<Ctx | null>(null)

/**
 * Topbar breadcrumb'ına sayfa-özel ek adımlar (ör. Toplantı grubu → toplantı) enjekte etmek için hafif
 * store. Menü tabanlı kısım (modül › sayfa) useActiveMenu'dan gelir; bu trail onun devamına eklenir.
 */
export function BreadcrumbProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [trail, setTrail] = useState<Crumb[]>([])
  const value = useMemo(() => ({ trail, setTrail }), [trail])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Topbar okur: aktif sayfanın ek breadcrumb adımları. */
export function useBreadcrumbTrail(): Crumb[] {
  return useContext(BreadcrumbContext)?.trail ?? []
}

/** Sayfa çağırır: ek breadcrumb adımlarını bildirir; unmount'ta temizler. */
export function useSetBreadcrumb(items: Crumb[]) {
  // DİKKAT: bağımlılığa context nesnesini (`ctx`) KOYMA — provider value'su her setTrail'de yeni referans
  // olur ve effect sonsuz döngüye girer (set → cleanup → set …). setTrail useState setter'ıdır ve
  // referansı sabittir → yalnız buna + içerik anahtarına (`key`) bağlan.
  const setTrail = useContext(BreadcrumbContext)?.setTrail
  const key = JSON.stringify(items)
  useEffect(() => {
    setTrail?.(items)
    return () => setTrail?.([])
    // items içeriği `key` (JSON) ile izlenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTrail, key])
}
